import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios instance
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('notimax_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors - NEVER auto-logout unless explicitly on login page
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // DO NOT auto-logout on 401 errors
    // The session should persist even if backend is temporarily unreachable
    // Only logout if user explicitly clicks logout button
    
    if (error.response?.status === 401) {
      const isLoginAttempt = error.config?.url?.includes('/auth/login');
      
      // Only handle 401 on actual login attempts (wrong credentials)
      // For all other 401s, just reject the promise but keep session
      if (isLoginAttempt) {
        // Login failed, don't touch localStorage
        return Promise.reject(error);
      }
      
      // For any other 401, log it but DON'T logout
      console.warn('API returned 401, but keeping session alive:', error.config?.url);
    }
    
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  getMe: () => apiClient.get('/auth/me'),
};

export const appointmentsAPI = {
  getAll: () => apiClient.get('/appointments'),
  create: (data) => apiClient.post('/appointments', data),
  update: (id, data) => apiClient.put(`/appointments/${id}`, data),
  delete: (id) => apiClient.delete(`/appointments/${id}`),
  updateStatus: (id, status) => apiClient.patch(`/appointments/${id}/status`, null, { params: { status } }),
  searchPatients: (query) => apiClient.get('/appointments/patients/search', { params: { q: query } }),
};

export const tagsAPI = {
  getAll: () => apiClient.get('/tags'),
  create: (data) => apiClient.post('/tags', data),
  update: (id, data) => apiClient.put(`/tags/${id}`, data),
  delete: (id) => apiClient.delete(`/tags/${id}`),
};

export const recommendationsAPI = {
  getByAppointment: (appointmentId) => apiClient.get(`/recommendations/${appointmentId}`),
  create: (data) => apiClient.post('/recommendations', data),
  delete: (id) => apiClient.delete(`/recommendations/${id}`),
};

export const profileAPI = {
  get: () => apiClient.get('/profile', {
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    },
    // Add timestamp to prevent caching
    params: { _t: new Date().getTime() }
  }),
  update: (data) => apiClient.put('/profile', data),
};

export const subscriptionsAPI = {
  getPlans: () => apiClient.get('/subscriptions/plans'),
  getStatus: () => apiClient.get('/subscriptions/status'),
  createStripeCheckout: (plan) => apiClient.post('/subscriptions/create-checkout-stripe', { plan }),
  createPayPalCheckout: (plan) => apiClient.post('/subscriptions/create-checkout-paypal', { plan }),
};

// Public API (no auth required) - using fetch directly to avoid axios instance issues
export const publicAPI = {
  confirmAppointment: async (params) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${BACKEND_URL}/api/public/confirm-appointment?${queryString}`;
      console.log('Fetching URL:', url);
      
      const response = await fetch(url);
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.log('Error response:', errorText);
        throw { response: { status: response.status, data: errorText } };
      }
      
      const data = await response.json();
      console.log('Success response:', data);
      return { data };
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  },
};

export const adminAPI = {
  getStats: () => apiClient.get('/admin/stats'),
  getUsers: () => apiClient.get('/admin/users'),
  getSubscriptions: () => apiClient.get('/admin/subscriptions'),
  updateUserRole: (userId, role) => apiClient.put(`/admin/users/${userId}/role`, { role }),
  updateUserSubscription: (userId, data) => apiClient.put(`/admin/users/${userId}/subscription`, data),
  deleteUser: (userId) => apiClient.delete(`/admin/users/${userId}`),
  getAppointmentStats: (period, date) => apiClient.get(`/admin/appointment-stats?period=${period}&date=${date}`),
};

export const branchesAPI = {
  getAll: () => apiClient.get('/branches'),
  create: (data) => apiClient.post('/branches', data),
  update: (id, data) => apiClient.put(`/branches/${id}`, data),
  delete: (id) => apiClient.delete(`/branches/${id}`),
};

export const servicesAPI = {
  getAll: () => apiClient.get('/services'),
  create: (data) => apiClient.post('/services', data),
  update: (id, data) => apiClient.put(`/services/${id}`, data),
  delete: (id) => apiClient.delete(`/services/${id}`),
};

export default apiClient;
